import { Injectable } from '@angular/core'
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects'
import { catchError, map, switchMap, of, tap, filter, mergeMap } from 'rxjs'
import { caseActions } from './case.actions'
import { DatasetApiService } from '../../shared/services/dataset-api.service'
import { ToasterService } from '../../shared/services/toaster.service'
import { Store } from '@ngrx/store'
import {
    selectCaseId,
    selectHasCaseId,
    selectCaseSegObjectPropertyMap,
    selectCaseSegObjectPropertyMapsForLoadedSegLayers,
    selectLoadedSegSources,
    selectLoadedSegObjectPropertyMaps,
    selectMarkerDimensionsByMapId,
} from './case.selectors'
import { RouterUtilsService } from '../../shared/services/router-utils.service'
import { MarkerCatalogService } from '../../shared/services/marker-catalog.service'
import { PlotDataCacheService } from '../../shared/services/plot-data-cache.service'
import { viewerActions } from '../viewer/viewer.actions'
import { selectionActions } from '../selection/selection.actions'
import {
    selectFreeSelectionName,
    selectHierarchicalSelection,
    selectSelectionType,
    selectUnsupervisedSelection,
} from '../selection/selection.selectors'
import {
    getHierarchicalRootSegSources,
    sameStringSet,
    SelectionType,
} from '../selection/selection.state'

@Injectable()
export class CaseEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly datasetApi: DatasetApiService,
        private readonly toaster: ToasterService,
        private readonly store: Store,
        private readonly routerUtils: RouterUtilsService,
        private readonly markerCatalog: MarkerCatalogService,
        private readonly plotDataCache: PlotDataCacheService
    ) {}

    selectCase$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(caseActions.selectCase),
                concatLatestFrom(() => this.store.select(selectHasCaseId)),
                switchMap(([{ caseId }, hasCaseId]) => {
                    return !hasCaseId
                        ? // User currently has no case open, go to case without updating viewer state
                          of(caseId)
                        : // User currently has case open, update viewer state before switching case
                          of(caseId).pipe(this.datasetApi.saveViewerState())
                }),
                tap(caseId => {
                    window.stop()
                    this.routerUtils.goToCase(caseId)
                })
            )
        },
        { dispatch: false }
    )

    reloadCase$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(caseActions.reloadCase),
                concatLatestFrom(() => this.store.select(selectCaseId)),
                filter(([, caseId]) => !!caseId),
                tap(([, caseId]) => {
                    this.routerUtils.goToCaseReload(caseId!)
                })
            )
        },
        { dispatch: false }
    )

    loadSegObjectPropertiesData$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadSegObjectPropertiesData),
            switchMap(({ caseId, segobjectpropertymap }) =>
                this.datasetApi.getSegObjectPropertiesData(caseId, segobjectpropertymap).pipe(
                    map(segobjectpropertiesdata => {
                        return caseActions.loadingSegObjectPropertiesDataSucceeded({
                            segobjectpropertiesdata,
                            segobjectpropertymap,
                        })
                    }),
                    catchError(error =>
                        of(
                            caseActions.loadingSegObjectPropertyMapsFailed({
                                error,
                                message: 'Error loading seg object properties data.',
                            })
                        )
                    )
                )
            )
        )
    })

    syncMarkerCatalog$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(caseActions.loadingSegObjectPropertiesDataSucceeded),
                tap(({ segobjectpropertiesdata, segobjectpropertymap }) => {
                    this.markerCatalog.setCatalog(
                        segobjectpropertiesdata.map(marker => ({
                            ...marker,
                            segobjectpropertymap:
                                marker.segobjectpropertymap ?? segobjectpropertymap,
                        }))
                    )
                    this.plotDataCache.clear()
                })
            )
        },
        { dispatch: false }
    )

    /** Keep the marker catalog aligned with remaining loaded maps after a toggle. */
    syncMarkerCatalogOnLoadedMapsChange$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(caseActions.toggleLoadedSegObjectPropertyMap),
                concatLatestFrom(() => [
                    this.store.select(selectLoadedSegObjectPropertyMaps),
                    this.store.select(selectMarkerDimensionsByMapId),
                ]),
                tap(([, loadedMaps, markerDimensionsByMapId]) => {
                    this.plotDataCache.clear()
                    if (loadedMaps.length === 0) {
                        this.markerCatalog.clearCatalog()
                        return
                    }
                    // Prefer the most recently loaded map as the catalog source (matches prior behavior).
                    const primary = loadedMaps[loadedMaps.length - 1]
                    const markers =
                        markerDimensionsByMapId[Number(primary.id)] ??
                        markerDimensionsByMapId[primary.id]
                    if (markers?.length) {
                        this.markerCatalog.setCatalog(markers)
                    } else {
                        this.markerCatalog.clearCatalog()
                    }
                })
            )
        },
        { dispatch: false }
    )

    clearMarkerCatalogOnLoadCase$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(caseActions.loadCase),
                tap(() => {
                    this.markerCatalog.clearCatalog()
                    this.plotDataCache.clear()
                })
            )
        },
        { dispatch: false }
    )

    clearInvalidSegObjectPropertyMap$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(viewerActions.setLocalLayerVisibility, viewerActions.configLoaded),
            concatLatestFrom(() => [
                this.store.select(selectCaseSegObjectPropertyMap),
                this.store.select(selectCaseSegObjectPropertyMapsForLoadedSegLayers),
            ]),
            filter(([, selected, available]) => {
                if (!selected) return false
                return !available.some(map => map.id === selected.id)
            }),
            switchMap(() => [
                caseActions.clearSegObjectPropertyMapSelection(),
                selectionActions.clearSelection({ allBuckets: true }),
            ])
        )
    })

    /**
     * Adding/removing intensity maps resets the working (unsaved) selection.
     * Keep buckets when a named free selection is active so the scatter plot can
     * re-highlight points for still-loaded maps.
     * Named hierarchical/unsupervised trees are cleared when the loaded map combo
     * no longer matches their create-time segSources lock.
     */
    clearSelectionOnLoadedMapsChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.toggleLoadedSegObjectPropertyMap),
            concatLatestFrom(() => [
                this.store.select(selectSelectionType),
                this.store.select(selectFreeSelectionName),
                this.store.select(selectLoadedSegSources),
                this.store.select(selectHierarchicalSelection),
                this.store.select(selectUnsupervisedSelection),
            ]),
            mergeMap(
                ([
                    ,
                    selectionType,
                    freeSelectionName,
                    loadedSegSources,
                    hierarchicalSelection,
                    unsupervisedSelection,
                ]) => {
                    const next = []
                    const keepNamedFreeSelection =
                        selectionType === SelectionType.Free && !!freeSelectionName
                    if (!keepNamedFreeSelection) {
                        next.push(selectionActions.clearSelection({ allBuckets: true }))
                    }

                    if (
                        selectionType === SelectionType.Hierarchical &&
                        hierarchicalSelection.name
                    ) {
                        const locked = getHierarchicalRootSegSources(hierarchicalSelection)
                        if (locked.length > 0 && !sameStringSet(locked, loadedSegSources)) {
                            next.push(selectionActions.clearActiveHierarchicalSelectionRoot())
                        }
                    }
                    if (
                        selectionType === SelectionType.Unsupervised &&
                        unsupervisedSelection.name
                    ) {
                        const locked = getHierarchicalRootSegSources(unsupervisedSelection)
                        if (locked.length > 0 && !sameStringSet(locked, loadedSegSources)) {
                            next.push(selectionActions.clearActiveUnsupervisedSelectionRoot())
                        }
                    }
                    return next
                }
            )
        )
    })

    clearMarkerCatalogOnInvalidMap$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(caseActions.clearSegObjectPropertyMapSelection),
                tap(() => {
                    this.markerCatalog.clearCatalog()
                    this.plotDataCache.clear()
                })
            )
        },
        { dispatch: false }
    )

    loadSegObjectPropertyMaps$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadCase),
            switchMap(({ caseId }) =>
                this.datasetApi.getSegObjectPropertyMaps(caseId).pipe(
                    map(segobjectpropertymaps =>
                        caseActions.loadingSegObjectPropertyMapsSucceeded({ segobjectpropertymaps })
                    ),
                    catchError(error => {
                        console.error('Error loading Seg Object Property Maps:', error) // Logs the error
                        return of(
                            caseActions.loadingSegObjectPropertyMapsFailed({
                                error,
                                message: 'Error loading segmentation property maps.',
                            })
                        )
                    })
                )
            )
        )
    })
    loadRecurrentNeighborhoodsResult$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadCase),
            switchMap(({ caseId }) =>
                this.datasetApi.getRecurrentNeighborhoodsResults(caseId).pipe(
                    map(rcn =>
                        caseActions.loadingRecurrentNeighborhoodsResultsSucceeded({
                            neighborhood: rcn,
                        })
                    ),
                    catchError(error =>
                        of(
                            caseActions.loadingRecurrentNeighborhoodsResultsFailed({
                                error,
                                message: 'Error loading recurrent neighborhood.',
                            })
                        )
                    )
                )
            )
        )
    })

    loadNeighborhoodEnrichmentResult$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadCase),
            switchMap(({ caseId }) =>
                this.datasetApi.getNeighborhoodEnrichmentResults(caseId).pipe(
                    map(neighborhoodEnrichment =>
                        caseActions.loadingNeighborhoodEnrichmentResultsSucceeded({
                            neighborhood: neighborhoodEnrichment,
                        })
                    ),
                    catchError(error =>
                        of(
                            caseActions.loadingNeighborhoodEnrichmentResultsFailed({
                                error,
                                message: 'Error loading Neighborhood Enrichment.',
                            })
                        )
                    )
                )
            )
        )
    })

    loadTsneResults$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadCase),
            switchMap(({ caseId }) =>
                this.datasetApi.getTsneResults(caseId).pipe(
                    map(tsne =>
                        caseActions.loadingTsneResultsSucceeded({
                            tsne: tsne,
                        })
                    ),
                    catchError(error =>
                        of(
                            caseActions.loadingTsneResultsFailed({
                                error,
                                message: 'Error loading TSNE.',
                            })
                        )
                    )
                )
            )
        )
    })

    loadUmapResults$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadCase),
            switchMap(({ caseId }) =>
                this.datasetApi.getUmapResults(caseId).pipe(
                    map(umap =>
                        caseActions.loadingUmapResultsSucceeded({
                            umap: umap,
                        })
                    ),
                    catchError(error =>
                        of(
                            caseActions.loadingUmapResultsFailed({
                                error,
                                message: 'Error loading UMAP.',
                            })
                        )
                    )
                )
            )
        )
    })

    loadAutoCellDetectionResult$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadCase),
            switchMap(({ caseId }) =>
                this.datasetApi.getAutoCellDetectionResults(caseId).pipe(
                    map(autoCellDetection =>
                        caseActions.loadingAutoCellDetectionResultsSucceeded({
                            autoCellDetection: autoCellDetection,
                        })
                    ),
                    catchError(error =>
                        of(
                            caseActions.loadingAutoCellDetectionResultsFailed({
                                error,
                                message: 'Error loading Auto Cell Detection.',
                            })
                        )
                    )
                )
            )
        )
    })

    loadPlottingResult$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadCase),
            switchMap(({ caseId }) =>
                this.datasetApi.getPlottingResults(caseId).pipe(
                    map(plottingResult =>
                        caseActions.loadingPlottingResultsSucceeded({
                            plotting: plottingResult,
                        })
                    ),
                    catchError(error =>
                        of(
                            caseActions.loadingPlottingResultsFailed({
                                error,
                                message: 'Error loading Plotting.',
                            })
                        )
                    )
                )
            )
        )
    })

    showLoadingErrorToast$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    caseActions.loadingMarkersFailed,
                    caseActions.loadingRecurrentNeighborhoodsResultsFailed,
                    caseActions.loadingNeighborhoodEnrichmentResultsFailed,
                    caseActions.loadingAutoCellDetectionResultsFailed,
                    caseActions.loadingPlottingResultsFailed
                ),
                tap(({ error, message }) => {
                    this.toaster.httpError(error, message)
                })
            )
        },
        { dispatch: false }
    )
}
